import { ethers } from 'ethers'
import { useCallback, useEffect, useState } from 'react'

import duneData from '../sunset/duneData.json'
import { CHAIN_NAMES, executeWithRetry } from './useMultiChainProvider'

// Reward pool addresses by chain
const REWARD_POOLS: { [chainId: number]: { address: string; symbol: string }[] } = {
  56: [{ address: '0x3dD417Ff4144bE35f203CB2CA569adF01Cd3574a', symbol: 'ATA/USDT' }],
  137: [{ address: '0x3dD417Ff4144bE35f203CB2CA569adF01Cd3574a', symbol: 'ATA/USDT' }]
}

// Minimal ABI for reward pool
const REWARD_POOL_ABI = [
  'function totalSupply() view returns (uint256)',
  'function stakingToken() view returns (address)',
  'function getAllRewardTokens() view returns (address[])',
  'function rewardData(address) view returns (address rewardsDistributor, uint256 rewardsDuration, uint256 periodFinish, uint256 rewardRate, uint256 lastUpdateTime, uint256 rewardPerTokenStored)',
  'function paused() view returns (bool)',
  'function earned(address account, address rewardsToken) view returns (uint256)'
]

const ERC20_ABI = [
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'function balanceOf(address) view returns (uint256)'
]

export interface RewardTokenData {
  address: string
  symbol: string
  decimals: number
  rewardRate: string
  periodFinish: number
  isActive: boolean
  contractBalance: string
  contractBalanceFormatted: number
}

export interface StakerRewardData {
  userAddress: string
  chainId: number
  rewards: {
    tokenAddress: string
    tokenSymbol: string
    earned: string
    earnedFormatted: number
    claimable: string
    claimableFormatted: number
    isRecoverable: boolean // true if contract has enough balance to pay this reward
  }[]
}

export interface RewardPoolData {
  chainId: number
  chain: string
  address: string
  symbol: string
  stakingToken: string
  totalStaked: string
  totalStakedFormatted: number
  rewardTokens: RewardTokenData[]
  stakerRewards: StakerRewardData[]
  paused: boolean
  error?: string
}

export function useRewardPoolData() {
  const [pools, setPools] = useState<RewardPoolData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const allPools: RewardPoolData[] = []

      for (const [chainIdStr, poolConfigs] of Object.entries(REWARD_POOLS)) {
        const chainId = parseInt(chainIdStr)

        for (const poolConfig of poolConfigs) {
          try {
            // Use executeWithRetry for automatic retry with exponential backoff
            const poolData = await executeWithRetry(chainId, async provider => {
              const contract = new ethers.Contract(poolConfig.address, REWARD_POOL_ABI, provider)

              // Fetch basic pool data
              const [totalSupply, stakingToken, rewardTokenAddresses, paused] = await Promise.all([
                contract.totalSupply(),
                contract.stakingToken(),
                contract.getAllRewardTokens(),
                contract.paused().catch(() => false)
              ])

              // Fetch reward token data
              const rewardTokens: RewardTokenData[] = []
              const now = Math.floor(Date.now() / 1000)

              for (const rewardTokenAddress of rewardTokenAddresses) {
                try {
                  const tokenContract = new ethers.Contract(rewardTokenAddress, ERC20_ABI, provider)
                  const [symbol, decimals, rewardData, contractBalance] = await Promise.all([
                    tokenContract.symbol(),
                    tokenContract.decimals(),
                    contract.rewardData(rewardTokenAddress),
                    tokenContract.balanceOf(poolConfig.address)
                  ])

                  const periodFinish = rewardData.periodFinish.toNumber()
                  const isActive = periodFinish > now

                  rewardTokens.push({
                    address: rewardTokenAddress,
                    symbol,
                    decimals,
                    rewardRate: ethers.utils.formatUnits(rewardData.rewardRate, decimals),
                    periodFinish,
                    isActive,
                    contractBalance: contractBalance.toString(),
                    contractBalanceFormatted: parseFloat(
                      ethers.utils.formatUnits(contractBalance, decimals)
                    )
                  })
                } catch (err) {
                  console.warn(`Failed to fetch reward token ${rewardTokenAddress}:`, err)
                }
              }

              const totalStakedFormatted = parseFloat(ethers.utils.formatUnits(totalSupply, 18))

              // Fetch earned rewards for each staker on this chain
              const chainStakers = duneData.farmStakers.filter(s => s.chainId === chainId)
              const stakerRewards: StakerRewardData[] = []

              // Track remaining balance for each reward token to calculate actual claimable
              const remainingBalances: Record<string, ethers.BigNumber> = {}
              for (const rt of rewardTokens) {
                remainingBalances[rt.address] = ethers.BigNumber.from(rt.contractBalance)
              }

              for (const staker of chainStakers) {
                try {
                  const rewards: StakerRewardData['rewards'] = []
                  for (const rewardToken of rewardTokens) {
                    const earned = await contract.earned(staker.userAddress, rewardToken.address)
                    const earnedFormatted = parseFloat(
                      ethers.utils.formatUnits(earned, rewardToken.decimals)
                    )

                    // Calculate actual claimable based on remaining contract balance
                    const remaining = remainingBalances[rewardToken.address]
                    const claimable = earned.gt(remaining) ? remaining : earned
                    const claimableFormatted = parseFloat(
                      ethers.utils.formatUnits(claimable, rewardToken.decimals)
                    )

                    // Deduct from remaining balance for next staker calculation
                    remainingBalances[rewardToken.address] = remaining.sub(claimable)

                    rewards.push({
                      tokenAddress: rewardToken.address,
                      tokenSymbol: rewardToken.symbol,
                      earned: earned.toString(),
                      earnedFormatted,
                      claimable: claimable.toString(),
                      claimableFormatted,
                      isRecoverable: earned.gt(0) && claimable.eq(earned)
                    })
                  }
                  stakerRewards.push({
                    userAddress: staker.userAddress,
                    chainId,
                    rewards
                  })
                } catch (err) {
                  console.warn(`Failed to fetch earned for staker ${staker.userAddress}:`, err)
                }
              }

              return {
                chainId,
                chain: CHAIN_NAMES[chainId] || `Chain ${chainId}`,
                address: poolConfig.address,
                symbol: poolConfig.symbol,
                stakingToken,
                totalStaked: totalSupply.toString(),
                totalStakedFormatted,
                rewardTokens,
                stakerRewards,
                paused
              }
            })

            allPools.push(poolData)
          } catch (err) {
            console.warn(
              `Failed to fetch pool ${poolConfig.address} on chain ${chainId} after retries:`,
              err
            )
            // Add pool with error state so user knows it failed
            allPools.push({
              chainId,
              chain: CHAIN_NAMES[chainId] || `Chain ${chainId}`,
              address: poolConfig.address,
              symbol: poolConfig.symbol,
              stakingToken: '',
              totalStaked: '0',
              totalStakedFormatted: 0,
              rewardTokens: [],
              stakerRewards: [],
              paused: false,
              error: err instanceof Error ? err.message : 'Failed to fetch'
            })
          }
        }
      }

      setPools(allPools)
    } catch (err) {
      console.error('Failed to fetch reward pool data:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { pools, loading, error, refetch: fetchData }
}
